import { useCallback } from 'react';

import { useAnalyticsUtilities } from '../hooks/useAnalyticsUtilities';
import { type AirActionTrackingLocation } from '../types';
import { type ShareUrlGeneratedComments, type ShareUrlGeneratedCustomFields } from './useTrackGeneratedShareUrl';
import { type ShareUrlUpdatedDownloads, type ShareUrlUpdatedVersionControl } from './useTrackShareUrlUpdated';

/**
 * Snapshot of the share link's current settings sent alongside a copy event.
 *
 * Mirrors the properties captured by `Generated a share url` plus the two
 * settings the V2 share modal added (`downloads`, `version_control`). All fields
 * are optional because some copy surfaces (e.g. the public share modal) don't
 * have the full link config on hand; those callers omit the settings entirely.
 * The MASHA-only fields (`hide_search_input` / `disable_view_settings` /
 * `disable_expanding_nested`) apply to boards & collections.
 */
export type ShareUrlCopiedSettingsParams = {
  metadata: boolean;
  custom_fields: ShareUrlGeneratedCustomFields;
  comments: ShareUrlGeneratedComments;
  downloads: ShareUrlUpdatedDownloads;
  version_control: ShareUrlUpdatedVersionControl;
  hide_search_input?: boolean;
  disable_view_settings?: boolean;
  disable_expanding_nested?: boolean;
};

export type TrackCopiedShareLinkParams = {
  id: string;
  shareId: string;
  type: 'asset' | 'clip' | 'board' | 'collection' | 'canvas';
  location: AirActionTrackingLocation;
} & Partial<ShareUrlCopiedSettingsParams>;

export const useTrackCopiedShareLink = () => {
  const { track } = useAnalyticsUtilities();

  const trackCopiedShareLink = useCallback(
    (params: TrackCopiedShareLinkParams) => {
      track(`Copied a share url`, params, { integrations: { 'Customer.io (Actions)': true, Intercom: true } });
    },
    [track],
  );

  return { trackCopiedShareLink };
};
